import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { moduleHref } from '../../module';
import { Loading, ErrorState, Empty, fmtDate } from '../../components/ui';
import { Panel } from '../../components/charts';
import {
  CX_LEVELS,
  LEVEL_NAME,
  RESULT_CHIP,
  RESULT_LABEL,
  fmtInt,
  floorLabel,
  type Summary,
} from './Dashboard';

/* ============================================================
   CxL1 – L5 — tabel butir uji.

   Seribu lima puluh baris pada proyek ini. Angka itu yang menentukan
   hampir semua keputusan di berkas ini.

   Pengelompokan, penyaringan, dan subtotalnya dihitung di server dan
   halaman menggambar apa yang diberikan; menariknya sebagai satu
   daftar datar lalu mengelompokkannya di sini berarti setiap
   perubahan saringan membayar ulang seribu baris di browser.

   Saringan hidup di URL, bukan di state komponen. Tabel yang tidak
   bisa ditempelkan ke pesan untuk rekan kerja adalah setengah fitur —
   dan dasbor perlu bisa menautkan langsung ke "yang mana".

   Kelompok dimulai TERTUTUP begitu tabelnya besar. Baris ringkasan
   tiap kelompok membawa lulus, total, dan persennya, jadi tabel yang
   terlipat seluruhnya tetap memberi gambaran lantai per lantai —
   sementara membuka 1.050 baris yang tidak diminta siapa pun hanya
   membuat halaman berat.
   ============================================================ */

type CxRow = {
  id: string;
  equipmentTag: string;
  type: string | null;
  floor: string | null;
  room: string | null;
  level: string;
  result: string;
  testedOn: string | null;
  reportFileId: string | null;
  reportNo: string | null;
  punchCount: number;
  openNcrIds: string[];
};

type CxGroup = {
  key: string;
  label: string;
  passed: number;
  failed: number;
  total: number;
  pct: number;
  rows: CxRow[];
};

type Items = {
  groups: CxGroup[];
  totals: {
    passed: number;
    failed: number;
    inProgress: number;
    notStarted: number;
    total: number;
    pct: number;
  };
  filtered: number;
};

const GROUPS: { key: string; label: string }[] = [
  { key: 'floor', label: 'Lantai' },
  { key: 'room', label: 'Ruang' },
  { key: 'level', label: 'Tingkat' },
  { key: 'result', label: 'Hasil' },
  { key: 'equipmentType', label: 'Jenis peralatan' },
  { key: 'none', label: 'Tanpa kelompok' },
];

const RESULTS = ['NOT_STARTED', 'IN_PROGRESS', 'PASSED', 'FAILED', 'PASSED_WITH_PUNCH'];

/** Tag selalu tampil: baris tanpa tag tidak bisa dijelaskan kepada siapa pun. */
const COLUMNS: { key: string; label: string }[] = [
  { key: 'type', label: 'Jenis' },
  { key: 'floor', label: 'Lantai' },
  { key: 'room', label: 'Ruang' },
  { key: 'level', label: 'Tingkat' },
  { key: 'result', label: 'Hasil' },
  { key: 'testedOn', label: 'Diuji' },
  { key: 'report', label: 'Laporan' },
  { key: 'punch', label: 'Punch' },
  { key: 'ncr', label: 'NCR' },
];

const COLS_KEY = 'munin.cx.levels.columns';
const OPEN_KEY = 'munin.cx.levels.open';

/**
 * Di bawah ambang ini kelompok dibuka sendiri. Orang yang sudah
 * menyaring sampai tersisa dua puluh baris tidak sedang ingin melihat
 * daftar kelompok, ia sedang mencari barisnya.
 */
const AUTO_OPEN_ROWS = 120;

function readJson<T extends object>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const v: unknown = JSON.parse(raw);
    return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as T) : fallback;
  } catch {
    // Pilihan kolom yang rusak bukan alasan menjatuhkan tabelnya.
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Mode privat atau kuota penuh: pilihannya tidak tersimpan, dan
    // tabelnya tetap jalan. Tidak ada yang perlu diberitahukan.
  }
}

/** Daftar dari query string, dan sebaliknya. */
function listOf(raw: string): string[] {
  return raw.split(',').filter((v) => v !== '');
}

export function Levels({ projectId }: { projectId: string }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<Items | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<CxRow | null>(null);
  const [cols, setCols] = useState<Record<string, boolean>>(() => readJson(COLS_KEY, {}));
  const [open, setOpen] = useState<Record<string, Record<string, boolean>>>(() =>
    readJson(OPEN_KEY, {}),
  );

  const groupBy = params.get('groupBy') ?? 'floor';
  const search = params.get('search') ?? '';
  const levels = params.get('levels') ?? '';
  const results = params.get('results') ?? '';
  const floors = params.get('floors') ?? '';
  const hasReport = params.get('hasReport') ?? '';
  const hasOpenNcr = params.get('hasOpenNcr') ?? '';
  const testedFrom = params.get('testedFrom') ?? '';
  const testedTo = params.get('testedTo') ?? '';

  const filtered =
    search !== '' ||
    levels !== '' ||
    results !== '' ||
    floors !== '' ||
    hasReport !== '' ||
    hasOpenNcr !== '' ||
    testedFrom !== '' ||
    testedTo !== '';

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  function toggleIn(key: string, value: string) {
    const current = listOf(params.get(key) ?? '');
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setParam(key, next.join(','));
  }

  const qs = new URLSearchParams({ projectId });
  if (groupBy !== 'none') qs.set('groupBy', groupBy);
  if (search) qs.set('search', search);
  if (levels) qs.set('levels', levels);
  if (results) qs.set('results', results);
  if (floors) qs.set('floors', floors);
  if (hasReport) qs.set('hasReport', hasReport);
  if (hasOpenNcr) qs.set('hasOpenNcr', hasOpenNcr);
  if (testedFrom) qs.set('testedFrom', testedFrom);
  if (testedTo) qs.set('testedTo', testedTo);
  const query = qs.toString();

  const load = useCallback(async () => {
    try {
      setData(await api.get<Items>(`/cx/items?${query}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Tabel commissioning tidak bisa dimuat.');
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  // Daftar lantai datang dari ringkasan, bukan dari baris yang sedang
  // tampil: pilihan saringan yang menyusut setiap kali disaring tidak
  // bisa dipakai untuk melebarkan saringan lagi.
  useEffect(() => {
    api
      .get<Summary>(`/cx/summary?projectId=${projectId}`)
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [projectId]);

  const shown = COLUMNS.filter((c) => cols[c.key] ?? true);
  const span = 1 + shown.length;
  const autoOpen = (data?.filtered ?? 0) <= AUTO_OPEN_ROWS;

  // Tanpa pengelompokan, kelompok 'all' ADALAH tabelnya. Melipatnya
  // hanya menyembunyikan satu-satunya hal yang diminta orang ketika
  // ia memilih "tanpa kelompok".
  function isOpen(key: string): boolean {
    if (groupBy === 'none') return true;
    return open[groupBy]?.[key] ?? autoOpen;
  }

  function toggleGroup(key: string) {
    const next = { ...open, [groupBy]: { ...(open[groupBy] ?? {}), [key]: !isOpen(key) } };
    setOpen(next);
    writeJson(OPEN_KEY, next);
  }

  function setAllGroups(value: boolean) {
    const per: Record<string, boolean> = {};
    for (const g of data?.groups ?? []) per[g.key] = value;
    const next = { ...open, [groupBy]: per };
    setOpen(next);
    writeJson(OPEN_KEY, next);
  }

  function toggleCol(key: string) {
    const next = { ...cols, [key]: !(cols[key] ?? true) };
    setCols(next);
    writeJson(COLS_KEY, next);
  }

  function cell(r: CxRow, key: string): ReactNode {
    switch (key) {
      case 'type':
        return r.type ?? '—';
      case 'floor':
        return floorLabel(r.floor);
      case 'room':
        return r.room ?? '—';
      case 'level':
        return <span title={LEVEL_NAME[r.level] ?? r.level}>{r.level}</span>;
      case 'result':
        return (
          <span className={`chip ${RESULT_CHIP[r.result] ?? ''}`}>
            {RESULT_LABEL[r.result] ?? r.result}
          </span>
        );
      case 'testedOn':
        return r.testedOn ? fmtDate(r.testedOn) : '—';
      case 'report':
        if (r.reportNo) return r.reportNo;
        return r.reportFileId ? <span className="chip good">berkas</span> : '—';
      case 'punch':
        // Baris yang belum diuji tidak punya nol butir punch, ia belum
        // dihitung. Nol yang ditulis di sini adalah nol yang diukur.
        return r.result === 'NOT_STARTED' ? '—' : fmtInt(r.punchCount);
      case 'ncr':
        if (r.openNcrIds.length === 0) return '—';
        return r.openNcrIds.map((id) => (
          <a
            key={id}
            className="chip crit"
            style={{ textDecoration: 'none', marginRight: 6 }}
            href={moduleHref('QM', `/ncrs/${id}`)}
            onClick={(e) => e.stopPropagation()}
          >
            NCR
          </a>
        ));
      default:
        return '—';
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>CxL1 – L5</h1>
          <p>
            Satu baris adalah satu peralatan pada satu tingkat. Hasil tanpa berkas laporan uji tidak
            memindahkan status — itulah gunanya tabel ini.
          </p>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      <Panel
        title="Saringan"
        note="Daftar saringan yang kosong berarti tidak menyaring, bukan tidak ada yang cocok."
        wide
      >
        <div className="filters">
          <label className="hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Kelompokkan menurut
            <select
              className="select"
              value={groupBy}
              onChange={(e) => setParam('groupBy', e.target.value)}
            >
              {GROUPS.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.label}
                </option>
              ))}
            </select>
          </label>

          <input
            className="input"
            placeholder="Cari tag, lantai, ruang, penyaksi…"
            defaultValue={search}
            key={search}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setParam('search', (e.target as HTMLInputElement).value.trim());
            }}
            aria-label="Cari butir uji"
          />

          <select
            className="select"
            value={floors}
            onChange={(e) => setParam('floors', e.target.value)}
            aria-label="Saring menurut lantai"
          >
            <option value="">Semua lantai</option>
            {(summary?.byFloor ?? []).map((f) => (
              <option key={f.floor} value={f.floor}>
                {floorLabel(f.floor)} — {fmtInt(f.total)} butir
              </option>
            ))}
          </select>

          <select
            className="select"
            value={hasReport}
            onChange={(e) => setParam('hasReport', e.target.value)}
            aria-label="Saring menurut berkas laporan"
          >
            <option value="">Laporan: semua</option>
            <option value="1">Ada berkas laporan</option>
            <option value="0">Tanpa berkas laporan</option>
          </select>

          <select
            className="select"
            value={hasOpenNcr}
            onChange={(e) => setParam('hasOpenNcr', e.target.value)}
            aria-label="Saring menurut NCR terbuka"
          >
            <option value="">NCR: semua</option>
            <option value="1">Ada NCR terbuka</option>
            <option value="0">Tanpa NCR terbuka</option>
          </select>

          <input
            className="input"
            type="date"
            title="Diuji sejak"
            value={testedFrom}
            onChange={(e) => setParam('testedFrom', e.target.value)}
          />
          <input
            className="input"
            type="date"
            title="Diuji sampai"
            value={testedTo}
            onChange={(e) => setParam('testedTo', e.target.value)}
          />
        </div>

        <div className="filters">
          <span className="hint">Tingkat</span>
          {CX_LEVELS.map((l) => (
            <label
              key={l}
              className="hint"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              title={LEVEL_NAME[l] ?? l}
            >
              <input
                type="checkbox"
                checked={listOf(levels).includes(l)}
                onChange={() => toggleIn('levels', l)}
              />
              {l}
            </label>
          ))}
        </div>

        <div className="filters">
          <span className="hint">Hasil</span>
          {RESULTS.map((r) => (
            <label key={r} className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={listOf(results).includes(r)}
                onChange={() => toggleIn('results', r)}
              />
              {RESULT_LABEL[r] ?? r}
            </label>
          ))}
        </div>

        <div className="filters" style={{ marginBottom: 0 }}>
          <span className="hint">Kolom</span>
          {COLUMNS.map((c) => (
            <label key={c.key} className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={cols[c.key] ?? true}
                onChange={() => toggleCol(c.key)}
              />
              {c.label}
            </label>
          ))}
          {filtered && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setParams(groupBy === 'floor' ? {} : { groupBy }, { replace: true })}
            >
              Bersihkan saringan
            </button>
          )}
        </div>
      </Panel>

      {!data ? (
        <Loading rows={6} />
      ) : (
        <>
          <div className="filters" style={{ alignItems: 'center' }}>
            <span className="hint">
              Menampilkan {fmtInt(data.filtered)}
              {summary ? ` dari ${fmtInt(summary.items)}` : ''} butir uji · {fmtInt(data.totals.passed)}{' '}
              lulus · {fmtInt(data.totals.failed)} gagal · {fmtInt(data.totals.inProgress)} berjalan ·{' '}
              {fmtInt(data.totals.notStarted)} belum mulai · {data.totals.pct}%
            </span>
            {groupBy !== 'none' && data.groups.length > 0 && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={() => setAllGroups(true)}>
                  Buka semua
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setAllGroups(false)}>
                  Tutup semua
                </button>
              </>
            )}
          </div>

          {data.filtered === 0 ? (
            <Empty
              title={filtered ? 'Tidak ada yang cocok' : 'Belum ada butir uji'}
              body={
                filtered
                  ? 'Tidak ada butir uji yang lolos saringan ini. Baris tanpa tanggal uji tidak pernah masuk rentang tanggal mana pun.'
                  : 'Butir uji dibuat dari register peralatan, lima tingkat per peralatan.'
              }
              action={
                filtered ? (
                  <button
                    className="btn btn-ghost"
                    onClick={() => setParams(groupBy === 'floor' ? {} : { groupBy }, { replace: true })}
                  >
                    Bersihkan saringan
                  </button>
                ) : undefined
              }
            />
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Tag</th>
                    {shown.map((c) => (
                      <th key={c.key}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                {data.groups.map((g) => (
                  <tbody key={g.key}>
                    {groupBy !== 'none' && (
                      <tr
                        onClick={() => toggleGroup(g.key)}
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && toggleGroup(g.key)}
                        aria-expanded={isOpen(g.key)}
                      >
                        <td colSpan={span}>
                          <span aria-hidden="true" style={{ marginRight: 8 }}>
                            {isOpen(g.key) ? '▾' : '▸'}
                          </span>
                          <b>{groupBy === 'floor' ? floorLabel(g.key) : g.label}</b>
                          <span className="hint" style={{ marginLeft: 12 }}>
                            {fmtInt(g.passed)} lulus / {fmtInt(g.total)} butir · {g.pct}%
                            {g.failed > 0 ? ` · ${fmtInt(g.failed)} gagal` : ''}
                          </span>
                        </td>
                      </tr>
                    )}
                    {isOpen(g.key) &&
                      g.rows.map((r) => (
                        <tr
                          key={r.id}
                          onClick={() => setEditing(r)}
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && setEditing(r)}
                        >
                          <td className="num">{r.equipmentTag}</td>
                          {shown.map((c) => (
                            <td key={c.key}>{cell(r, c.key)}</td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                ))}
              </table>
            </div>
          )}
        </>
      )}

      {editing && (
        <ResultDialog
          row={editing}
          onClose={() => setEditing(null)}
          onDone={(message) => {
            setEditing(null);
            setNotice(message);
            void load();
          }}
        />
      )}
    </>
  );
}

/* ============================================================
   Mencatat hasil
   ============================================================ */

/** Cermin RESULT_TRANSITIONS di commissioning-core.ts. */
const NEXT: Record<string, string[]> = {
  NOT_STARTED: ['IN_PROGRESS'],
  IN_PROGRESS: ['PASSED', 'PASSED_WITH_PUNCH', 'FAILED'],
  PASSED: ['IN_PROGRESS'],
  FAILED: ['IN_PROGRESS'],
  PASSED_WITH_PUNCH: ['PASSED', 'IN_PROGRESS'],
};

const NEXT_HINT: Record<string, string> = {
  IN_PROGRESS: 'Uji sedang berjalan, atau uji ulang setelah peralatan dimodifikasi.',
  PASSED: 'Lulus bersih. Menuntut laporan uji, dan tidak boleh ada butir punch terbuka.',
  PASSED_WITH_PUNCH: 'Diterima dengan sisa butir punch yang dijadwalkan. Tetap menuntut laporan uji.',
  FAILED: 'Gagal. Tingkat berikutnya tertahan sampai uji ulang lulus.',
};

type Gate = { blockingLevel?: string | null; blockingNcrs?: { id: string; ncrNo: string }[] };

/**
 * Gerbang tingkat dan penolakan penandaan hasil sama-sama datang
 * sebagai `details.gate`, jadi keduanya dibedakan dari ISI-nya. Hanya
 * gerbang tingkat yang bisa diterobos MANAGER; syarat laporan uji
 * tidak bisa diterobos siapa pun, dan menawarkan kotak alasan untuk
 * hal yang tidak bisa diterobos hanya menyuruh orang mengetik dua
 * kali.
 */
function gateOf(details: unknown): Gate | null {
  if (details === null || typeof details !== 'object') return null;
  const gate = (details as { gate?: unknown }).gate;
  if (gate === null || typeof gate !== 'object') return null;
  const g = gate as Gate;
  const hasLevel = typeof g.blockingLevel === 'string';
  const hasNcrs = Array.isArray(g.blockingNcrs);
  return hasLevel || hasNcrs ? g : null;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function ResultDialog({
  row,
  onClose,
  onDone,
}: {
  row: CxRow;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const allowed = NEXT[row.result] ?? [];
  const [result, setResult] = useState<string>(allowed[0] ?? 'IN_PROGRESS');
  const [testedOn, setTestedOn] = useState(row.testedOn ?? today());
  const [reportNo, setReportNo] = useState(row.reportNo ?? '');
  const [reportFileId, setReportFileId] = useState('');
  const [punchCount, setPunchCount] = useState('');
  const [witnesses, setWitnesses] = useState('');
  const [reason, setReason] = useState('');
  const [gate, setGate] = useState<Gate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const passing = result === 'PASSED' || result === 'PASSED_WITH_PUNCH';
  // Kelulusan menuntut nomor laporan di sini, dan berkas laporan di
  // server. Dua-duanya sengaja: nomor adalah apa yang tertulis di
  // sertifikat, berkas adalah apa yang membuktikannya.
  const blocked = busy || testedOn === '' || (passing && reportNo.trim() === '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key !== 'Tab' || !ref.current) return;
      // Jebak fokus: dialog yang bisa ditinggalkan keyboard adalah
      // dialog untuk pengguna tetikus saja.
      const items = ref.current.querySelectorAll<HTMLElement>(
        'button, input, textarea, select, [href], [tabindex]:not([tabindex="-1"])',
      );
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { result, testedOn };
      if (reportNo.trim() !== '') body.reportNo = reportNo.trim();
      if (reportFileId.trim() !== '') body.reportFileId = reportFileId.trim();

      const punch = Number.parseInt(punchCount, 10);
      if (punchCount.trim() !== '' && Number.isFinite(punch)) body.punchCount = punch;

      // Daftar penyaksi yang kosong TIDAK dikirim. Server mengganti
      // seluruh daftar dengan apa yang dikirim, jadi mengirim daftar
      // kosong akan menghapus penyaksi yang sudah tercatat.
      const named = witnesses
        .split(',')
        .map((w) => w.trim())
        .filter((w) => w !== '');
      if (named.length > 0) body.witnesses = named;

      if (reason.trim() !== '') body.override = { reason: reason.trim() };

      const out = await api.post<{
        equipmentTag: string;
        level: string;
        from: string;
        to: string;
        overridden: string | null;
      }>(`/cx/items/${row.id}/result`, body);

      const moved = `${out.equipmentTag} ${out.level}: ${RESULT_LABEL[out.from] ?? out.from} → ${
        RESULT_LABEL[out.to] ?? out.to
      }`;
      onDone(out.overridden ? `${moved} — gerbang diterobos, alasannya tersimpan.` : moved);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        setGate(gateOf(e.details));
      } else {
        setError('Hasil tidak bisa dicatat.');
        setGate(null);
      }
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Catat hasil uji"
        ref={ref}
      >
        <h2>Catat hasil</h2>
        <p className="sub">
          {row.equipmentTag} · {row.level} — {LEVEL_NAME[row.level] ?? row.level} · sekarang{' '}
          {RESULT_LABEL[row.result] ?? row.result}
        </p>

        {error && (
          <div className="banner err" role="alert">
            {error}
            {gate && (
              <div style={{ marginTop: 8 }}>
                {typeof gate.blockingLevel === 'string' && gate.blockingLevel !== '' && (
                  <span className="chip crit" style={{ marginRight: 6 }}>
                    {gate.blockingLevel}
                  </span>
                )}
                {(gate.blockingNcrs ?? []).map((n) => (
                  <a
                    key={n.id}
                    className="chip crit"
                    style={{ textDecoration: 'none', marginRight: 6 }}
                    href={moduleHref('QM', `/ncrs/${n.id}`)}
                  >
                    {n.ncrNo}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {allowed.length === 0 ? (
          <p className="hint">Tidak ada hasil yang bisa dicatat dari keadaan ini.</p>
        ) : (
          <div className="codes" role="radiogroup" aria-label="Hasil uji">
            {allowed.map((r) => (
              <label key={r} className={`code-opt ${result === r ? 'sel' : ''}`}>
                <input
                  type="radio"
                  name="cx-result"
                  value={r}
                  checked={result === r}
                  onChange={() => setResult(r)}
                />
                <span>
                  <b>{RESULT_LABEL[r] ?? r}</b>
                  <span>{NEXT_HINT[r] ?? ''}</span>
                </span>
              </label>
            ))}
          </div>
        )}

        <label className="field">
          <span>Tanggal uji</span>
          <input
            className="input"
            type="date"
            value={testedOn}
            onChange={(e) => setTestedOn(e.target.value)}
            required
          />
        </label>

        <label className="field">
          <span>Nomor laporan {passing ? '(wajib)' : '(opsional)'}</span>
          <input
            className="input"
            value={reportNo}
            onChange={(e) => setReportNo(e.target.value)}
            placeholder="misalnya CX-L3-CRAH01-001"
          />
          {passing && reportNo.trim() === '' && (
            <span className="hint">
              Kelulusan tanpa nomor laporan adalah kelulusan yang tidak bisa ditelusuri enam bulan
              lagi. Sertifikat RFS disusun dari laporan ini.
            </span>
          )}
        </label>

        {passing && row.reportFileId === null && (
          <label className="field">
            <span>Berkas laporan uji</span>
            <input
              className="input"
              value={reportFileId}
              onChange={(e) => setReportFileId(e.target.value)}
              placeholder="id berkas laporan di Drive proyek"
            />
            <span className="hint">
              Baris ini belum punya berkas laporan, dan kelulusan tidak akan tersimpan tanpa berkas.
            </span>
          </label>
        )}

        {result === 'PASSED_WITH_PUNCH' && (
          <label className="field">
            <span>Butir punch terbuka</span>
            <input
              className="input"
              type="number"
              min={1}
              value={punchCount}
              onChange={(e) => setPunchCount(e.target.value)}
              placeholder={String(row.punchCount)}
            />
            <span className="hint">
              Diabaikan kalau baris ini sudah punya daftar butir punch sendiri — daftarnya yang
              benar, bukan angka yang diketik di sini.
            </span>
          </label>
        )}

        {result === 'PASSED' && row.punchCount > 0 && (
          <p className="hint">
            Baris ini masih membawa {fmtInt(row.punchCount)} butir punch terbuka, jadi hasilnya
            bernama lulus dengan punch.
          </p>
        )}

        <label className="field">
          <span>Penyaksi (opsional)</span>
          <input
            className="input"
            value={witnesses}
            onChange={(e) => setWitnesses(e.target.value)}
            placeholder="Nama, dipisah koma"
          />
          <span className="hint">Dibiarkan kosong berarti penyaksi yang sudah tercatat tidak diubah.</span>
        </label>

        {gate && (
          <label className="field">
            <span>Alasan menerobos gerbang</span>
            <textarea
              className="textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Kenapa peralatan ini boleh naik tingkat sekalipun tertahan?"
            />
            <span className="hint">
              Menerobos gerbang menuntut peran MANAGER pada cx., dan alasannya tersimpan di jejak
              audit — di sanalah satu-satunya tempat ia bisa dibaca lagi.
            </span>
          </label>
        )}

        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Batal
          </button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={blocked || allowed.length === 0}
          >
            {busy ? 'Menyimpan…' : 'Simpan hasil'}
          </button>
        </div>
      </div>
    </div>
  );
}
